/**
 * Component Tokens
 *
 * Generated from figma-exports/component.css.
 * Edit the source CSS and re-run the figma-token-sync skill — do not hand-edit.
 *
 * Structure: palette.ts → tokens-{dark,light}.ts → component-tokens.ts
 *  - `t.*` refs point at semantic tokens (mode-aware via createTokens parameter).
 *  - `d.*` refs point at mode-agnostic detail tokens (sizing, spacing, cornerRadius).
 *  - `neutrals.clear` is used for empty `var(--)` refs in the CSS export.
 *  - String literals (e.g. '2px') are used for CSS literal pixel values.
 */

import type tokensDark from './tokens-dark';
import tokensDarkDefault from './tokens-dark';
import { palette } from './palette';
import { detailTokens } from './detail-tokens';

const { neutrals } = palette;
const d = detailTokens;

type Tokens = typeof tokensDark;

export const createTokens = <T extends Tokens>(tokens: T) => {
  const t = tokens;

  return {
    avatar: {
      colors: {
        background: {
          container: {
            resting: t.background.accent[2][2],
            hover: t.background.accent[2][4],
            selected: t.background.brand.dark[3],
          },
          status: {
            success: t.background.semantic.success.base,
            error: t.background.semantic.error.base,
          },
        },
        stroke: {
          container: {
            resting: neutrals.clear,
            hover: t.stroke.brand[1],
            selected: t.stroke.brand.base,
          },
          status: {
            cutOut: t.background.neutral.canvas.base,
          },
        },
        assets: {
          resting: t.content.assets.neutral[1],
          hover: t.content.assets.neutral.base,
          selected: t.content.assets.neutral.base,
        },
        text: {
          resting: t.content.text.on.color.light.dark,
          hover: t.content.assets.on.color.light.dark,
          selected: t.content.text.on.color.light.dark,
        },
      },
      details: {
        size: {
          container: {
            xl: d.sizing.sizeTokens['3xl'],
            lg: d.sizing.sizeTokens.lg,
            md: d.sizing.sizeTokens.sm,
            sm: d.sizing.sizeTokens.xs,
          },
          status: {
            xl: d.sizing.asset.lg,
            lg: d.sizing.asset.sm,
            md: d.sizing.asset.xs,
            sm: d.sizing.asset['3xs'],
          },
        },
        corner: {
          radius: {
            container: d.cornerRadius.pill,
            notification: d.cornerRadius.pill,
          },
        },
        asset: {
          size: {
            xl: d.sizing.asset['2xl'],
            lg: d.sizing.asset.lg,
            md: d.sizing.asset.sm,
            sm: d.sizing.asset.sm,
          },
        },
      },
    },
    badges: {
      colors: {
        solid: {
          brand: {
            background: {
              standard: t.background.accent[1].base,
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.on.color.light.light,
            },
            assets: {
              default: t.content.assets.on.color.light.light,
            },
          },
          accent: {
            background: {
              standard: t.background.accent[3].base,
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.on.color.dark.light,
            },
            assets: {
              default: t.content.assets.on.color.dark.light,
            },
          },
          neutral: {
            background: {
              standard: t.background.neutral.raised.base,
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.on.color.dark.light,
            },
            assets: {
              default: t.content.assets.on.color.dark.light,
            },
          },
          success: {
            background: {
              standard: t.background.semantic.success.base,
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.on.color.dark.dark,
            },
            assets: {
              default: t.content.assets.on.color.dark.dark,
            },
          },
          warning: {
            background: {
              standard: t.background.semantic.warning.base,
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.on.color.dark.dark,
            },
            assets: {
              default: t.content.assets.on.color.dark.dark,
            },
          },
          error: {
            background: {
              standard: t.background.semantic.error[1],
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.on.color.light.dark,
            },
            assets: {
              default: t.content.assets.on.color.light.dark,
            },
          },
          info: {
            background: {
              standard: t.background.semantic.info.base,
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.on.color.dark.dark,
            },
            assets: {
              default: t.content.assets.on.color.dark.dark,
            },
          },
          medium: {
            background: {
              standard: t.background.semantic.suggest.base,
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.on.color.dark.dark,
            },
            assets: {
              default: t.content.assets.on.color.dark.dark,
            },
          },
        },
        outline: {
          brand: {
            background: {
              standard: t.background.accent[1][5],
            },
            stroke: {
              default: t.stroke.accent[1][1],
            },
            text: {
              default: t.content.text.on.color.light.dark,
            },
            assets: {
              default: t.content.assets.on.color.light.dark,
            },
          },
          accent: {
            background: {
              standard: t.background.accent[3][5],
            },
            stroke: {
              default: t.stroke.accent[3][1],
            },
            text: {
              default: t.content.text.on.color.light.dark,
            },
            assets: {
              default: t.content.assets.on.color.light.dark,
            },
          },
          neutral: {
            background: {
              standard: t.background.neutral.raised[5],
            },
            stroke: {
              default: t.stroke.neutral[4],
            },
            assets: {
              default: t.content.assets.neutral.base,
            },
            text: {
              default: t.content.text.neutral.base,
            },
          },
          success: {
            background: {
              standard: t.background.semantic.success[5],
            },
            stroke: {
              default: t.stroke.semantic.success[1],
            },
            text: {
              default: t.content.text.on.color.light.dark,
            },
            assets: {
              default: t.content.assets.on.color.light.dark,
            },
          },
          warning: {
            background: {
              standard: t.background.semantic.warning[5],
            },
            stroke: {
              default: t.stroke.semantic.warning[1],
            },
            text: {
              default: t.content.text.on.color.light.dark,
            },
            assets: {
              default: t.content.assets.on.color.light.dark,
            },
          },
          error: {
            background: {
              standard: t.background.semantic.error[5],
            },
            stroke: {
              default: t.stroke.semantic.error[1],
            },
            text: {
              default: t.content.text.on.color.light.dark,
            },
            assets: {
              default: t.content.assets.on.color.light.dark,
            },
          },
          info: {
            background: {
              standard: t.background.semantic.info[5],
            },
            stroke: {
              default: t.stroke.semantic.info[1],
            },
            text: {
              default: t.content.text.on.color.light.dark,
            },
            assets: {
              default: t.content.assets.on.color.light.dark,
            },
          },
          medium: {
            background: {
              standard: t.background.semantic.suggest[5],
            },
            stroke: {
              default: t.stroke.semantic.suggest[1],
            },
            text: {
              default: t.content.text.on.color.light.dark,
            },
            assets: {
              default: t.content.assets.on.color.light.dark,
            },
          },
        },
        subtle: {
          brand: {
            background: {
              standard: t.background.accent[1][4],
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.on.color.light.dark,
            },
            assets: {
              default: t.content.assets.on.color.light.dark,
            },
          },
          accent: {
            background: {
              standard: t.background.accent[3][4],
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.on.color.light.dark,
            },
            assets: {
              default: t.content.assets.on.color.light.dark,
            },
          },
          neutral: {
            background: {
              standard: t.background.neutral.raised[4],
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.neutral.base,
            },
            assets: {
              default: t.content.assets.neutral.base,
            },
          },
          success: {
            background: {
              standard: t.background.semantic.success[4],
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.on.color.light.dark,
            },
            assets: {
              default: t.content.assets.on.color.light.dark,
            },
          },
          warning: {
            background: {
              standard: t.background.semantic.warning[4],
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.on.color.light.dark,
            },
            assets: {
              default: t.content.assets.on.color.light.dark,
            },
          },
          error: {
            background: {
              standard: t.background.semantic.error[4],
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.on.color.light.dark,
            },
            assets: {
              default: t.content.assets.on.color.light.dark,
            },
          },
          info: {
            background: {
              standard: t.background.semantic.info[4],
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.on.color.light.dark,
            },
            assets: {
              default: t.content.assets.on.color.light.dark,
            },
          },
          medium: {
            background: {
              standard: t.background.semantic.suggest[4],
            },
            stroke: {
              default: t.stroke.basic.clear,
            },
            text: {
              default: t.content.text.on.color.light.dark,
            },
            assets: {
              default: t.content.assets.on.color.light.dark,
            },
          },
        },
      },
      details: {
        height: {
          sm: d.sizing.sizeTokens['3xs'],
          md: d.sizing.sizeTokens['2xs'],
          lg: d.sizing.sizeTokens.xs,
        },
        horizontalPadding: {
          sm: d.spacing.padding['2xs'],
          md: d.spacing.padding['2xs'],
          lg: d.spacing.padding.xs,
        },
        spaceBetween: {
          sm: d.spacing.spaceBetween['3xs'],
          md: d.spacing.spaceBetween['3xs'],
          lg: d.spacing.spaceBetween['2xs'],
        },
        corner: {
          radius: {
            corner: {
              radius: d.cornerRadius.pill,
            },
          },
        },
        asset: {
          size: {
            sm: d.sizing.asset.xs,
            base: d.sizing.asset.xs,
            lg: d.sizing.asset.sm,
          },
        },
      },
    },
    breadcrumbs: {
      colors: {
        primary: {
          text: {
            resting: t.content.text.neutral[2],
            hover: t.content.text.neutral.base,
            selected: t.content.text.on.color.light.dark,
          },
          assets: {
            resting: t.content.assets.accent[1].base,
            hover: t.content.assets.neutral.base,
            selected: t.content.assets.accent[1].base,
          },
        },
      },
      details: {
        spaceBetween: {
          sm: d.spacing.spaceBetween['2xs'],
          lg: d.spacing.spaceBetween.xs,
        },
        asset: {
          size: {
            sm: d.sizing.asset.xs,
            lg: d.sizing.asset.sm,
          },
        },
      },
    },
    buttons: {
      colors: {
        coreIcon: {
          primary: {
            background: {
              resting: t.background.brand.blue[1],
              hover: t.background.brand.blue.base,
              selected: t.background.brand.blue[1],
            },
            stroke: {
              resting: t.stroke.basic.clear,
              hover: t.stroke.basic.clear,
              selected: t.stroke.basic.clear,
            },
            text: {
              resting: t.content.text.on.color.light.light,
              hover: t.content.text.on.color.light.light,
              selected: t.content.text.on.color.light.light,
            },
            assets: {
              resting: t.content.assets.on.color.light.light,
              onHover: t.content.assets.on.color.light.light,
              selected: t.content.assets.on.color.light.light,
            },
          },
          secondary: {
            background: {
              resting: t.background.accent[1][5],
              hover: t.background.accent[1][4],
              selected: t.background.accent[1][5],
            },
            stroke: {
              resting: t.stroke.basic.clear,
              hover: t.stroke.basic.clear,
              selected: t.stroke.basic.clear,
            },
            text: {
              resting: t.content.text.neutral.base,
              hover: t.content.text.neutral.base,
              selected: t.content.text.neutral.base,
            },
            assets: {
              resting: t.content.assets.neutral[1],
              hover: t.content.assets.neutral.base,
              selected: t.content.assets.neutral.base,
            },
          },
          outline: {
            background: {
              resting: t.background.basic.clear,
              hover: t.background.neutral.raised[5],
              selected: t.background.neutral.raised[6],
            },
            stroke: {
              resting: t.stroke.neutral[2],
              hover: t.stroke.neutral.base,
              selected: t.stroke.neutral[1],
            },
            text: {
              resting: t.content.text.on.color.light.dark,
              hover: t.content.text.on.color.light.dark,
              selected: t.content.text.on.color.light.dark,
            },
            assets: {
              resting: t.content.assets.neutral[2],
              hover: t.content.assets.neutral.base,
              selected: t.content.assets.neutral.base,
            },
          },
          ghost: {
            background: {
              resting: t.background.basic.clear,
              hover: t.background.neutral.raised[5],
              selected: t.background.neutral.raised[6],
            },
            stroke: {
              resting: t.stroke.basic.clear,
              hover: t.stroke.basic.clear,
              selected: t.stroke.basic.clear,
            },
            text: {
              resting: t.content.text.neutral[1],
              hover: t.content.text.on.color.light.dark,
              selected: t.content.text.on.color.light.dark,
            },
            assets: {
              resting: t.content.assets.on.color.light.dark,
              hover: t.content.text.on.color.light.dark,
              selected: t.content.assets.on.color.light.dark,
            },
          },
          destructive: {
            background: {
              resting: t.background.semantic.error[1],
              hover: t.background.semantic.error.base,
              selected: t.background.semantic.error[1],
            },
            stroke: {
              resting: t.stroke.basic.clear,
              hover: t.stroke.basic.clear,
              selected: t.stroke.basic.clear,
            },
            text: {
              resting: t.content.text.on.color.light.light,
              hover: t.content.text.on.color.light.light,
              selected: t.content.text.on.color.light.light,
            },
            assets: {
              resting: t.content.assets.on.color.light.light,
              hover: t.content.assets.on.color.light.light,
              selected: t.content.assets.on.color.light.light,
            },
          },
        },
        link: {
          primary: {
            underlineDetail: {
              resting: t.stroke.basic.clear,
              hover: t.stroke.basic.clear,
              selected: t.stroke.basic.clear,
            },
            text: {
              resting: t.content.text.accent[14].base,
              hover: t.content.text.neutral.base,
              selected: t.content.text.neutral.base,
            },
            assets: {
              resting: t.content.assets.accent[14].base,
              hover: t.content.assets.neutral[1],
              selected: t.content.assets.neutral.base,
            },
          },
          secondary: {
            underlineDetail: {
              resting: t.stroke.basic.clear,
              hover: t.stroke.basic.clear,
              selected: t.stroke.basic.clear,
            },
            text: {
              resting: t.content.text.neutral.base,
              hover: t.content.text.neutral[1],
              selected: t.content.text.neutral.base,
            },
            assets: {
              resting: t.content.assets.neutral.base,
              hover: t.content.assets.neutral[1],
              selected: t.content.assets.neutral.base,
            },
          },
          destructive: {
            underlineDetail: {
              resting: t.stroke.basic.clear,
              hover: t.stroke.basic.clear,
              selected: t.stroke.basic.clear,
            },
            text: {
              resting: t.content.text.semantic.error.base,
              hover: t.content.text.semantic.error[1],
              selected: t.content.text.semantic.error[2],
            },
            assets: {
              resting: t.content.assets.semantic.error.base,
              hover: t.content.assets.semantic.error[1],
              selected: t.content.assets.semantic.error[2],
            },
          },
          orange: {
            underlineDetail: {
              resting: t.stroke.basic.clear,
              hover: t.stroke.basic.clear,
              selected: t.stroke.basic.clear,
            },
            text: {
              resting: t.content.text.semantic.suggest.base,
              hover: t.content.text.semantic.suggest[1],
              selected: t.content.text.semantic.suggest.base,
            },
            assets: {
              resting: t.content.assets.semantic.suggest.base,
              hover: t.content.assets.semantic.suggest[1],
              selected: t.content.assets.semantic.suggest.base,
            },
          },
        },
      },
      details: {
        height: {
          coreIcon: {
            xl: d.sizing.sizeTokens.lg,
            lg: d.sizing.sizeTokens.base,
            md: d.sizing.sizeTokens.sm,
            sm: d.sizing.sizeTokens.xs,
            xs: d.sizing.sizeTokens['3xs'],
          },
          underlineDetail: {
            sm: '1px',
            md: '1px',
            lg: '1px',
            xl: '1px',
          },
        },
        horizontalPadding: {
          sm: d.spacing.padding.xs,
          md: d.spacing.padding.sm,
          lg: d.spacing.padding.base,
          xl: d.spacing.padding.base,
        },
        spaceBetween: {
          horizontal: {
            sm: d.spacing.spaceBetween['2xs'],
            md: d.spacing.spaceBetween['2xs'],
            lg: d.spacing.spaceBetween.xs,
            xl: d.spacing.spaceBetween.xs,
          },
          vertical: {
            sm: d.spacing.spaceBetween.zero,
            md: d.spacing.spaceBetween.zero,
            lg: d.spacing.spaceBetween['3xs'],
            xl: d.spacing.spaceBetween['3xs'],
          },
        },
        corner: {
          radius: {
            base: d.cornerRadius.primary,
            alternate: d.cornerRadius.sharp,
            xs: d.cornerRadius.sm,
            pill: d.cornerRadius.pill,
          },
        },
        asset: {
          size: {
            xl: d.sizing.asset.mdBase,
            lg: d.sizing.asset.mdBase,
            md: d.sizing.asset.sm,
            sm: d.sizing.asset.sm,
            xs: d.sizing.asset.sm,
          },
        },
      },
    },
    calendar: {
      colors: {
        container: {
          background: {
            default: t.background.brand.dark.base,
          },
          stroke: {
            default: t.stroke.neutral[5],
          },
        },
        header: {
          text: {
            default: t.content.text.neutral[1],
          },
        },
        label: {
          text: {
            default: t.content.text.neutral[3],
          },
        },
        date: {
          background: {
            resting: t.background.neutral.midToWhite.base,
            hover: t.background.brand.blue[3],
            selected: t.background.brand.blue.base,
            today: t.background.neutral.inverse.raisedToBrand[2],
            range: t.background.brand.blue[4],
          },
          stroke: {
            resting: t.stroke.basic.clear,
            hover: t.stroke.basic.clear,
            selected: t.stroke.basic.clear,
            today: t.stroke.neutral[3],
          },
          text: {
            resting: t.content.text.neutral[1],
            hover: t.content.text.on.color.light.dark,
            selected: t.content.text.on.color.light.light,
            today: t.content.text.on.color.light.dark,
          },
        },
      },
      details: {
        height: {
          default: d.sizing.sizeTokens.sm,
        },
        corner: {
          radius: {
            default: d.cornerRadius.sm,
          },
        },
        horizontalPadding: {
          header: d.spacing.padding.xs,
          content: d.spacing.padding.sm,
        },
        verticalPadding: {
          default: d.spacing.padding.sm,
          inside: d.spacing.padding.xs,
        },
        spaceBetween: {
          default: d.spacing.spaceBetween.xs,
          secondary: d.spacing.spaceBetween.zero,
          content: d.spacing.spaceBetween['2xs'],
        },
      },
    },
    chip: {
      /**
       * `ChipStatic` component tokens — keyed by (type × variant).
       *
       * Mirrors Figma `chip/colors/static/{Type}/{Variant}/{property}/{state}`.
       * Adding/removing a variant or type requires adding the matching row here
       * AND adding the kebab-cased string to the const tuples in
       * `components/Chip/constants.ts`. The MUI theme registration in
       * `MuiChip.theme.ts` iterates those tuples, so theme + tokens stay in
       * lockstep.
       *
       * `variant: 'primary'` resolves Figma's `Brand` token slot.
       * `variant: 'high-alert' / 'medium-alert' / 'light' / 'subtle-attention'`
       * are new and have no Badge counterpart.
       */
      // Every color resolves to a semantic palette token — no hex literals.
      // Where the Figma frame's spec has no exact-match palette token, the
      // closest existing token is used (the actual rendered hex will diverge
      // slightly from Figma until palette is extended with chip-specific
      // entries by the figma-token-sync workflow).
      //
      // Outline: bg `t.background.basic.clear`, stroke + asset carry the
      // variant color.
      // Transparent: bg AND stroke both `clear`; text + asset use lightened
      // variant tints from `t.content.text.semantic.*` / `t.content.text.accent.*`.
      // Every chip cell points at an existing `t.backgroundO.*`, `t.strokeO.*`,
      // `t.contentO.*` semantic token — the dark-mode design system already
      // exposes the solid `*O` palette stops Figma uses for chips. Only two
      // chip-specific colours don't have a wider semantic role and live under
      // `t.chip.*`: the static-filled-error background (crimsonO[700]) and the
      // interactive field-label cyan (cryoWakeA[1000]).
      static: {
        filled: {
          primary: {
            background: { standard: t.backgroundO.brand.blue.base },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.content.text.on.color.light.light },
            asset: { default: t.content.assets.on.color.light.light },
          },
          accent: {
            background: { standard: t.background.accent[3].base },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.content.text.on.color.dark.dark },
            asset: { default: t.content.assets.on.color.dark.dark },
          },
          neutral: {
            background: { standard: t.backgroundO.neutral.mid[1] },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.content.text.on.color.light.light },
            asset: { default: t.content.assets.on.color.light.light },
          },
          success: {
            background: { standard: t.background.semantic.success.base },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.content.text.on.color.dark.dark },
            asset: { default: t.content.assets.on.color.dark.dark },
          },
          'high-alert': {
            background: { standard: t.contentO.text.semantic.warning[1] },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.content.text.on.color.dark.dark },
            asset: { default: t.content.assets.on.color.dark.dark },
          },
          'medium-alert': {
            background: { standard: t.background.semantic.suggest.base },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.content.text.on.color.dark.dark },
            asset: { default: t.content.assets.on.color.dark.dark },
          },
          error: {
            // Static filled error uses crimsonO[700] — the design system's
            // general `t.backgroundO.semantic.error.*` paths all live on
            // malachorO, a different red hue. `t.chip.static.errorFilledBackground`
            // is the chip-specific exception.
            background: { standard: t.chip.static.errorFilledBackground },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.content.text.on.color.light.light },
            asset: { default: t.content.assets.on.color.light.light },
          },
          light: {
            background: { standard: t.strokeO.accent[1].base },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.content.text.on.color.dark.dark },
            asset: { default: t.content.assets.on.color.dark.dark },
          },
          'subtle-attention': {
            background: { standard: t.strokeO.accent[2][3] },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.content.text.on.color.light.light },
            asset: { default: t.content.assets.on.color.light.light },
          },
        },
        outline: {
          primary: {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.strokeO.accent[1][2] },
            text: { default: t.content.text.on.color.light.dark },
            asset: { default: t.content.text.on.color.light.dark },
          },
          accent: {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.strokeO.accent[3][1] },
            text: { default: t.content.text.on.color.light.dark },
            asset: { default: t.content.text.on.color.light.dark },
          },
          neutral: {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.strokeO.neutral.mid[1] },
            text: { default: t.content.text.on.color.light.dark },
            asset: { default: t.content.text.on.color.light.dark },
          },
          success: {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.strokeO.semantic.success[2] },
            text: { default: t.content.text.on.color.light.dark },
            asset: { default: t.content.text.on.color.light.dark },
          },
          'high-alert': {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.strokeO.semantic.warning[2] },
            text: { default: t.content.text.on.color.light.dark },
            asset: { default: t.content.text.on.color.light.dark },
          },
          'medium-alert': {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.strokeO.semantic.suggest[3] },
            text: { default: t.content.text.on.color.light.dark },
            asset: { default: t.content.text.on.color.light.dark },
          },
          error: {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.strokeO.semantic.error[3] },
            text: { default: t.content.text.on.color.light.dark },
            asset: { default: t.content.text.on.color.light.dark },
          },
          light: {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.strokeO.accent[14][3] },
            text: { default: t.content.text.on.color.light.dark },
            asset: { default: t.content.text.on.color.light.dark },
          },
          'subtle-attention': {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.strokeO.accent[2][1] },
            text: { default: t.content.text.on.color.light.dark },
            asset: { default: t.content.text.on.color.light.dark },
          },
        },
        transparent: {
          primary: {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.strokeO.accent[1].base },
            asset: { default: t.strokeO.accent[1].base },
          },
          accent: {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.strokeO.accent[3].base },
            asset: { default: t.strokeO.accent[3].base },
          },
          neutral: {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.contentO.text.static[1] },
            asset: { default: t.contentO.text.static[1] },
          },
          success: {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.content.text.semantic.success[3] },
            asset: { default: t.content.text.semantic.success[3] },
          },
          'high-alert': {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.contentO.text.semantic.warning[2][2] },
            asset: { default: t.contentO.text.semantic.warning[2][2] },
          },
          'medium-alert': {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.content.text.semantic.suggest.base },
            asset: { default: t.content.text.semantic.suggest.base },
          },
          error: {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.content.text.semantic.error[1] },
            asset: { default: t.content.text.semantic.error[1] },
          },
          light: {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.strokeO.accent[14][1] },
            asset: { default: t.strokeO.accent[14][1] },
          },
          'subtle-attention': {
            background: { standard: t.background.basic.clear },
            stroke: { default: t.stroke.basic.clear },
            text: { default: t.backgroundO.accent[2][4] },
            asset: { default: t.backgroundO.accent[2][4] },
          },
        },
      },
      // ─── Interactive — variant × state grid. fieldLabel is a chip-specific
      // cyan exposed via `t.chip.interactive.fieldLabel`; everything else uses
      // existing dark-mode semantic tokens.
      interactive: {
        primary: {
          resting: {
            background: t.backgroundO.brand.blue.d1,
            stroke: t.strokeO.accent[1][5],
            label: t.content.text.on.color.light.light,
            fieldLabel: t.chip.interactive.fieldLabel,
            asset: t.content.assets.on.color.light.light,
          },
          hover: {
            background: t.backgroundO.brand.blue.base,
            stroke: t.stroke.semantic.info.base,
            label: t.content.text.on.color.light.light,
            fieldLabel: t.chip.interactive.fieldLabel,
            asset: t.content.assets.on.color.light.light,
          },
          selected: {
            background: t.backgroundO.brand.blue.d2,
            stroke: t.stroke.semantic.info.base,
            label: t.content.text.on.color.light.light,
            fieldLabel: t.chip.interactive.fieldLabel,
            asset: t.content.assets.on.color.light.light,
          },
        },
        neutral: {
          resting: {
            background: t.backgroundO.neutral.mid.d1,
            stroke: t.strokeO.neutral.mid[3],
            label: t.content.text.on.color.light.light,
            fieldLabel: t.chip.interactive.fieldLabel,
            asset: t.content.assets.on.color.light.light,
          },
          hover: {
            background: t.backgroundO.neutral.mid[1],
            stroke: t.strokeO.neutral.mid[1],
            label: t.content.text.on.color.light.light,
            fieldLabel: t.chip.interactive.fieldLabel,
            asset: t.content.assets.on.color.light.light,
          },
          selected: {
            background: t.backgroundO.neutral.mid.d2,
            stroke: t.strokeO.neutral.mid[1],
            label: t.content.text.on.color.light.light,
            fieldLabel: t.chip.interactive.fieldLabel,
            asset: t.content.assets.on.color.light.light,
          },
        },
        error: {
          resting: {
            background: t.strokeO.semantic.error[5],
            stroke: t.strokeO.semantic.error[1],
            label: t.content.text.on.color.light.light,
            fieldLabel: t.chip.interactive.fieldLabel,
            asset: t.content.assets.on.color.light.light,
          },
          hover: {
            background: t.strokeO.semantic.error[4],
            stroke: t.strokeO.semantic.error.base,
            label: t.content.text.on.color.light.light,
            fieldLabel: t.chip.interactive.fieldLabel,
            asset: t.content.assets.on.color.light.light,
          },
          selected: {
            background: t.backgroundO.semantic.error.d4,
            stroke: t.strokeO.semantic.error.base,
            label: t.content.text.on.color.light.light,
            fieldLabel: t.chip.interactive.fieldLabel,
            asset: t.content.assets.on.color.light.light,
          },
        },
      },
      colors: {
        basic: {
          solid: {
            brand: {
              background: { standard: t.background.accent[6].base },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.on.color.light.dark },
              assets: { default: t.content.assets.on.color.light.dark },
            },
            accent: {
              background: { standard: t.background.accent[3].base },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.on.color.dark.dark },
              assets: {
                default: t.content.assets.on.color.dark.light,
                defaultAlt: t.content.assets.on.color.dark.dark,
              },
            },
            neutral: {
              background: { standard: t.background.neutral.raised.base },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.on.color.dark.light },
              assets: { default: t.content.assets.on.color.dark.light },
            },
            success: {
              background: { standard: neutrals.clear },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.on.color.dark.dark },
              assets: { default: t.content.assets.on.color.dark.dark },
            },
            warning: {
              background: { standard: neutrals.clear },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.on.color.dark.dark },
              assets: { default: t.content.assets.on.color.dark.dark },
            },
            error: {
              background: { standard: neutrals.clear },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.on.color.light.light },
              assets: { default: t.content.assets.on.color.light.light },
            },
            info: {
              background: { standard: neutrals.clear },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.on.color.light.light },
              assets: { default: t.content.assets.on.color.light.light },
            },
            medium: {
              background: { standard: t.background.semantic.suggest.base },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.on.color.dark.dark },
              assets: { default: t.content.assets.on.color.dark.dark },
            },
          },
          outline: {
            brand: {
              background: { standard: t.background.accent[1][5] },
              stroke: { default: t.stroke.accent[1][1] },
              text: { default: t.content.text.on.color.light.dark },
              assets: { default: t.content.assets.on.color.light.dark },
            },
            accent: {
              background: { standard: t.background.accent[3][5] },
              stroke: { default: t.stroke.accent[3][1] },
              text: { default: t.content.text.on.color.light.dark },
              assets: { default: t.content.assets.on.color.light.dark },
            },
            neutral: {
              background: { standard: t.background.neutral.raised[5] },
              stroke: { default: t.stroke.neutral[4] },
              text: { default: t.content.text.neutral.base },
              assets: { default: t.content.assets.neutral.base },
            },
            success: {
              background: { standard: t.background.semantic.success[5] },
              stroke: { default: t.stroke.semantic.success[1] },
              text: { default: t.content.text.on.color.light.dark },
              assets: { default: t.content.assets.on.color.light.dark },
            },
            warning: {
              background: { standard: t.background.semantic.warning[5] },
              stroke: { default: t.stroke.semantic.warning[1] },
              text: { default: t.content.text.on.color.light.dark },
              assets: { default: t.content.assets.on.color.light.dark },
            },
            error: {
              background: { standard: t.background.semantic.error[5] },
              stroke: { default: t.stroke.semantic.error[1] },
              text: { default: t.content.text.on.color.light.dark },
              assets: { default: t.content.assets.on.color.light.dark },
            },
            info: {
              background: { standard: t.background.semantic.info[5] },
              stroke: { default: t.stroke.semantic.info[1] },
              text: { default: t.content.text.on.color.light.dark },
              assets: { default: t.content.assets.on.color.light.dark },
            },
            medium: {
              background: { standard: t.background.semantic.suggest[5] },
              stroke: { default: t.stroke.semantic.suggest[1] },
              text: { default: t.content.text.on.color.light.dark },
              assets: { default: t.content.assets.on.color.light.dark },
            },
          },
          semitransparent: {
            brand: {
              background: { standard: t.background.accent[1][4] },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.on.color.light.dark },
              assets: { default: t.content.assets.on.color.light.dark },
            },
            accent: {
              background: { standard: t.background.accent[3][4] },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.on.color.light.dark },
              assets: { default: t.content.assets.on.color.light.dark },
            },
            neutral: {
              background: { standard: t.background.neutral.raised[4] },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.neutral.base },
              assets: { default: t.content.assets.neutral.base },
            },
            success: {
              background: { standard: t.background.semantic.success[4] },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.on.color.light.dark },
              assets: { default: t.content.assets.on.color.light.dark },
            },
            warning: {
              background: { standard: t.background.semantic.warning[4] },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.on.color.light.dark },
              assets: { default: t.content.assets.on.color.light.dark },
            },
            error: {
              background: { standard: t.background.semantic.error[5] },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.on.color.light.dark },
              assets: { default: t.content.assets.on.color.light.dark },
            },
            info: {
              background: { standard: t.background.semantic.info[4] },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.on.color.light.dark },
              assets: { default: t.content.assets.on.color.light.dark },
            },
            medium: {
              background: { standard: t.background.semantic.suggest[4] },
              stroke: { default: t.stroke.basic.clear },
              text: { default: t.content.text.on.color.light.dark },
              assets: { default: t.content.assets.on.color.light.dark },
            },
          },
        },
        clickable: {
          outline: {
            background: {
              resting: t.background.basic.clear,
              hover: t.background.secondary[4],
              selected: t.background.secondary[5],
            },
            stroke: {
              resting: t.stroke.semantic.info[2],
              hover: t.stroke.semantic.info.base,
              selected: t.stroke.semantic.info[2],
            },
            text: { default: t.content.text.on.color.light.dark },
            assets: {
              light: {
                light: t.content.assets.on.color.light.light,
                dark: t.content.assets.on.color.light.dark,
              },
            },
          },
          filled: {
            background: {
              resting: t.background.brand.blue[1],
              hover: t.background.brand.blue.base,
              selected: t.background.brand.blue[1],
            },
            stroke: {
              resting: t.stroke.accent[1][3],
              hover: t.stroke.accent[1].base,
              selected: t.stroke.accent[1].base,
            },
            text: { default: t.content.text.on.color.light.light },
          },
        },
        deletable: {
          neutral: {
            background: {
              resting: t.background.neutral.raised[4],
              hover: t.background.neutral.raised[4],
              selected: t.background.neutral.raised[5],
            },
            stroke: {
              resting: t.stroke.neutral[5],
              hover: t.stroke.neutral[2],
              selected: t.stroke.neutral[4],
            },
            text: {
              labelBase: t.content.text.on.color.light.dark,
              staticBase: t.content.text.on.color.light.dark,
            },
            assets: {
              leadIconBase: t.content.assets.on.color.light.dark,
              closeBase: t.content.assets.on.color.light.dark,
            },
          },
          neutralAlt: {
            background: {
              resting: t.background.neutral.raised[5],
              hover: t.background.neutral.raised[5],
              selected: t.background.neutral.raised[7],
            },
            stroke: {
              resting: t.stroke.neutral[5],
              hover: t.stroke.neutral[2],
              selected: t.stroke.neutral[4],
            },
            text: {
              labelBase: t.content.text.on.color.light.dark,
              staticBase: t.content.text.neutral[3],
            },
            assets: {
              leadIconBase: t.content.assets.on.color.light.dark,
              closeBase: t.content.assets.on.color.light.dark,
            },
          },
          brand: {
            background: {
              resting: t.background.neutral.mid[4],
              hover: t.background.neutral.mid[4],
              selected: t.background.neutral.mid[5],
            },
            stroke: {
              resting: t.stroke.neutral[5],
              hover: t.stroke.neutral[2],
              selected: t.stroke.neutral[4],
            },
            text: {
              labelBase: t.content.text.on.color.light.dark,
              staticBase: t.content.text.neutral[3],
            },
            assets: {
              leadIconBase: t.content.assets.on.color.light.dark,
              closeBase: t.content.assets.neutral[3],
            },
          },
          accent: {
            background: {
              resting: t.background.accent[1][3],
              hover: t.background.accent[1][3],
              selected: t.background.accent[1][4],
            },
            stroke: {
              resting: t.stroke.accent[1][2],
              hover: t.stroke.accent[1][1],
              selected: t.stroke.accent[1][3],
            },
            text: {
              labelBase: t.content.text.on.color.light.dark,
              labelSelected: t.content.text.on.color.light.light,
              staticBase: t.content.text.static[3],
            },
            assets: {
              leadIconBase: t.content.assets.on.color.light.dark,
              closeBase: t.content.assets.neutral[3],
            },
          },
          info: {
            background: {
              resting: t.background.semantic.info[4],
              hover: t.background.semantic.info[4],
              selected: t.background.semantic.info[5],
            },
            stroke: {
              resting: t.stroke.semantic.info[4],
              hover: t.stroke.semantic.info[1],
              selected: t.stroke.semantic.info[2],
            },
            text: {
              labelBase: t.content.text.on.color.light.dark,
              staticBase: t.content.text.static[3],
            },
            assets: {
              leadIconBase: t.content.assets.on.color.light.dark,
              closeBase: t.content.assets.neutral[3],
            },
          },
          success: {
            background: {
              resting: t.background.semantic.success[3],
              hover: t.background.semantic.success[4],
              selected: t.background.semantic.success[5],
            },
            stroke: {
              resting: t.stroke.semantic.success[2],
              hover: t.stroke.semantic.success[3],
              selected: t.stroke.semantic.success[2],
            },
            text: {
              labelBase: t.content.text.on.color.light.dark,
              staticBase: t.content.text.neutral[3],
            },
            assets: {
              leadIconBase: t.content.assets.on.color.light.dark,
              closeBase: t.content.assets.neutral[3],
            },
          },
          warning: {
            background: {
              resting: t.background.semantic.warning[3],
              hover: t.background.semantic.warning[4],
              selected: t.background.semantic.warning[3],
            },
            stroke: {
              resting: t.stroke.semantic.warning[2],
              hover: t.stroke.semantic.warning[3],
              selected: t.stroke.semantic.warning[2],
            },
            text: {
              labelBase: t.content.text.on.color.light.dark,
              staticBase: t.content.text.neutral[3],
            },
            assets: {
              leadIconBase: t.content.assets.on.color.light.dark,
              closeBase: t.content.assets.neutral[3],
            },
          },
          error: {
            background: {
              resting: t.background.semantic.error[3],
              hover: t.background.semantic.error[4],
              selected: t.background.semantic.error[3],
            },
            stroke: {
              resting: t.stroke.semantic.error[2],
              hover: t.stroke.semantic.error[3],
              selected: t.stroke.semantic.error[2],
            },
            text: {
              labelBase: t.content.text.on.color.light.dark,
              staticBase: t.content.text.neutral[3],
            },
            assets: {
              leadIconBase: t.content.assets.on.color.light.dark,
              closeBase: t.content.assets.neutral[3],
            },
          },
          condition: {
            background: {
              resting: t.background.accent[6][2],
              hover: t.background.accent[6][1],
              selected: t.background.accent[6][2],
            },
            stroke: {
              resting: t.stroke.accent[1][4],
              hover: t.stroke.accent[1][3],
              selected: t.stroke.accent[1][2],
            },
            text: {
              labelBase: t.content.text.on.color.light.dark,
              labelBlue: t.content.text.accent[14].base,
              labelGreen: t.content.text.semantic.success.base,
              staticBase: t.content.text.neutral[3],
            },
            assets: {
              leadIconBase: t.content.assets.on.color.light.dark,
              closeBase: t.content.assets.neutral[3],
            },
          },
        },
      },
    },
    icons: {
      size: {
        xs: '12px',
        base: '18px',
        lg: '24px',
        sm: '16px',
        xl: '40px',
      },
    },
    indicators: {
      colors: {
        primary: {
          background: {
            resting: t.backgroundO.accent[6].base,
          },
          content: {
            text: {
              resting: t.content.text.on.color.light.light,
            },
          },
          stroke: {
            resting: t.strokeO.semantic.info[4],
          },
        },
        neutral: {
          background: {
            resting: t.backgroundO.neutral.mid[1],
          },
          content: {
            text: {
              resting: t.content.text.on.color.light.light,
            },
          },
          stroke: {
            resting: t.strokeO.neutral.mid[1],
          },
        },
        alert: {
          background: {
            resting: t.backgroundO.semantic.error.d2,
          },
          content: {
            text: {
              resting: t.content.text.on.color.light.light,
            },
          },
          stroke: {
            resting: t.strokeO.semantic.error[2],
          },
        },
        dark: {
          background: {
            resting: t.backgroundO.neutral.mid.d3,
          },
          content: {
            text: {
              resting: t.contentO.text.accent[16][2],
              secondary: t.contentO.assets.accent[3][2],
            },
          },
          stroke: {
            resting: t.strokeO.brand[3],
          },
        },
      },
      details: {
        height: {
          lg: d.sizing.sizeTokens['3xs'],
          sm: d.sizing.sizeTokens['4xs'],
        },
        horizontalPadding: {
          sm: d.spacing.padding['2xs'],
          lg: d.spacing.padding.xs,
        },
        corner: {
          radius: {
            corner: {
              radius: d.cornerRadius.primary,
            },
          },
        },
      },
    },
    inputs: {
      colors: {
        normal: {
          background: {
            resting: neutrals.clear,
            hover: neutrals.clear,
            selected: neutrals.clear,
          },
          stroke: {
            resting: t.stroke.neutral[3],
            hover: t.stroke.neutral[1],
            selected: t.stroke.semantic.info.base,
          },
          text: {
            label: {
              resting: t.content.text.neutral[1],
              hover: t.content.text.neutral.base,
              filled: t.content.text.neutral[1],
            },
            labelSelected: t.content.text.neutral.base,
            conditional: {
              default: t.content.text.neutral[1],
            },
            inputText: {
              resting: t.content.text.neutral[1],
              hover: t.content.text.neutral[1],
              selected: t.content.text.neutral.base,
              filled: t.content.text.neutral.base,
            },
            staticText: {
              default: t.content.text.neutral[1],
            },
            helper: {
              default: t.content.text.neutral[1],
            },
          },
          assets: {
            input: {
              resting: t.content.assets.on.color.light.dark,
              hover: t.content.assets.on.color.light.dark,
              selected: t.content.assets.neutral.base,
            },
            tooltip: {
              resting: t.content.assets.neutral[2],
              hover: t.content.assets.neutral[1],
              selected: t.content.assets.neutral.base,
            },
          },
        },
        error: {
          background: {
            resting: neutrals.clear,
            hover: neutrals.clear,
            selected: neutrals.clear,
          },
          stroke: {
            resting: t.stroke.semantic.error[2],
            hover: t.stroke.semantic.error[1],
            selected: t.stroke.semantic.error[1],
          },
          text: {
            label: {
              resting: t.content.text.neutral[1],
              hover: t.content.text.neutral.base,
              filled: t.content.text.neutral[1],
            },
            labelSelected: t.content.text.neutral.base,
            conditional: {
              default: t.content.text.neutral[1],
            },
            inputText: {
              resting: t.content.text.neutral[1],
              hover: t.content.text.neutral[1],
              selected: t.content.text.neutral.base,
              filled: t.content.text.neutral.base,
            },
            staticText: {
              default: t.content.text.neutral[1],
            },
            helper: {
              default: t.content.text.semantic.error.base,
            },
          },
          assets: {
            input: {
              resting: t.content.assets.neutral[1],
              hover: t.content.assets.neutral[1],
              selected: t.content.assets.neutral.base,
            },
            tooltip: {
              resting: t.content.assets.neutral[2],
              hover: t.content.assets.neutral[1],
              selected: t.content.assets.neutral.base,
            },
          },
        },
        success: {
          background: {
            resting: neutrals.clear,
            hover: neutrals.clear,
            selected: neutrals.clear,
          },
          stroke: {
            resting: t.stroke.semantic.success[2],
            hover: t.stroke.semantic.success.base,
            selected: t.stroke.semantic.success.base,
          },
          text: {
            label: {
              resting: t.content.text.neutral[1],
              hover: t.content.text.neutral.base,
              filled: t.content.text.neutral[1],
            },
            labelSelected: t.content.text.neutral.base,
            conditional: {
              default: t.content.text.neutral[1],
            },
            inputText: {
              resting: t.content.text.neutral[1],
              hover: t.content.text.neutral[1],
              selected: t.content.text.neutral.base,
              filled: t.content.text.neutral.base,
            },
            staticText: {
              default: t.content.text.neutral[1],
            },
            helper: {
              default: t.content.text.semantic.success[1],
            },
          },
          assets: {
            input: {
              resting: t.content.assets.neutral[1],
              hover: t.content.assets.neutral[1],
              selected: t.content.assets.neutral.base,
            },
            tooltip: {
              resting: t.content.assets.neutral[2],
              hover: t.content.assets.neutral[1],
              selected: t.content.assets.neutral.base,
            },
          },
        },
      },
      details: {
        height: {
          input: {
            xl: d.sizing.sizeTokens.lg,
            lg: d.sizing.sizeTokens.base,
            md: d.sizing.sizeTokens.sm,
            sm: d.sizing.sizeTokens.xs,
          },
          text: {
            area: {
              base: d.sizing.sizeTokens['3xl'],
            },
          },
        },
        horizontalPadding: {
          left: {
            lg: d.spacing.padding.sm,
            md: d.spacing.padding.sm,
            sm: d.spacing.padding.xs,
          },
          right: {
            lg: d.spacing.padding['3xs'],
            md: d.spacing.padding['4xs'],
            sm: d.spacing.padding.zero,
          },
          inside: {
            lg: d.spacing.padding.xs,
            md: d.spacing.padding.xs,
            sm: d.spacing.padding.xs,
          },
        },
        verticalPadding: {
          default: d.spacing.padding.xs,
        },
        spaceBetween: {
          horizontal: {
            lg: d.spacing.spaceBetween.xs,
            md: d.spacing.spaceBetween.xs,
            sm: d.spacing.spaceBetween.xs,
            xs: d.spacing.spaceBetween['3xs'],
            zero: d.spacing.spaceBetween.zero,
          },
          vertical: {
            lg: d.spacing.spaceBetween.xs,
            md: d.spacing.spaceBetween.xs,
            sm: d.spacing.spaceBetween['2xs'],
          },
        },
        corner: {
          radius: {
            primary: d.cornerRadius.primary,
          },
        },
        asset: {
          size: {
            lg: d.sizing.asset.mdBase,
            md: d.sizing.asset.sm,
            sm: d.sizing.asset.sm,
          },
        },
      },
    },
    jsonTree: {
      color: {
        text: {
          primary: {
            resting: t.content.text.accent[1].base,
            hover: t.content.text.neutral.base,
          },
          secondary: {
            resting: t.content.text.neutral[2],
            hover: t.content.text.neutral.base,
          },
        },
        asset: {
          editor: {
            hover: t.content.assets.neutral.base,
            resting: t.content.assets.brand.base,
          },
          treeview: {
            default: t.content.assets.neutral.base,
            discoveredField: t.content.assets.accent[5].base,
          },
        },
        background: {
          editor: {
            default: t.background.neutral.inverse.raised.base,
          },
          treeview: {
            resting: t.background.basic.clear,
            hover: t.background.neutral.mid[3],
            selected: t.background.neutral.midToBrand[3],
          },
        },
        stroke: {
          editor: {
            default: t.stroke.neutral.midToBrand.base,
          },
          resting: t.background.basic.clear,
          hover: t.background.neutral.mid[2],
          treeview: {
            default: t.stroke.neutral[4],
            selected: t.stroke.accent[1].base,
          },
        },
      },
      details: {
        horizontal: {
          spacing: {
            default: d.spacing.spaceBetween['2xs'],
          },
        },
        vertical: {
          spacing: {
            default: d.spacing.spaceBetween.xs,
          },
        },
      },
    },
    navigation: {
      colors: {
        background: {
          containerBase: t.backgroundO.neutral.canvas.panel,
          profileMenu: t.backgroundO.neutral.canvas.base,
        },
        stroke: {
          containerBase: t.background.neutral.raised[5],
        },
        text: {
          resting: t.content.text.on.color.light.dark,
          userResting: t.content.text.neutral.base,
          hover: t.content.text.neutral.base,
          selected: t.contentO.text.accent[14][3],
          selectedSubtle: t.content.text.on.color.light.dark,
          static: t.content.text.neutral[3],
        },
        assets: {
          resting: t.content.assets.neutral[2],
          hover: t.content.assets.on.color.light.dark,
          selected: t.contentO.assets.accent[14].base,
          selectedSubtle: t.content.text.on.color.light.dark,
          static: t.content.assets.on.color.light.dark,
        },
        backgroundRowItems: {
          resting: t.background.basic.clear,
          hover: t.background.neutral.raised[5],
          selected: t.background.accent[1][4],
          selectedSubtle: t.stroke.neutral[5],
          topNavSelected: t.background.basic.clear,
        },
        strokeRowItems: {
          resting: t.stroke.basic.clear,
          hover: t.stroke.basic.clear,
          selected: t.stroke.basic.clear,
          topNavSelected: t.stroke.basic.clear,
        },
        strokeDetailLine: {
          subMenu: t.content.assets.accent[1].base,
          resting: t.stroke.basic.clear,
          hover: t.stroke.basic.clear,
          active: t.stroke.accent[1].base,
        },
      },
      details: {
        height: {
          width: {
            container: {
              topNav: d.sizing.sizeTokens['3xl'],
              sideNav: {
                expanded: '256px',
              },
              floating: '320px',
            },
            items: {
              sideNav: d.sizing.sizeTokens.lg,
              subNav: d.sizing.sizeTokens.sm,
              topNav: d.sizing.sizeTokens.base,
              activeDetail: d.sizing.dividers.details.seconary,
            },
          },
        },
        horizontalPadding: {
          containerMain: d.spacing.padding.xl,
          containerSecondary: d.spacing.padding.base,
          nav: {
            item: {
              mainRow: d.spacing.padding.sm,
              subMenu: d.spacing.padding['2xl'],
            },
          },
        },
        verticalPadding: {
          base: d.spacing.padding.base,
          activeDetail: d.spacing.padding['4xs'],
          subMenu: d.spacing.padding.zero,
        },
        spaceBetween: {
          sections: {
            topNav: d.spacing.spaceBetween['3xl'],
            leftNav: d.spacing.spaceBetween['3xl'],
          },
          items: {
            leftNav: d.spacing.spaceBetween['3xs'],
            topNav: d.spacing.spaceBetween['3xs'],
            subMenu: d.spacing.spaceBetween.zero,
          },
          content: {
            leftNav: d.spacing.spaceBetween.sm,
            textStack: d.spacing.spaceBetween.zero,
          },
        },
        corner: {
          radius: {
            container: d.cornerRadius.sharp,
            altContainer: d.cornerRadius.primary,
            row: {
              item: d.cornerRadius.xs,
            },
          },
        },
        asset: {
          size: {
            base: d.sizing.asset.lg,
            sm: d.sizing.asset.sm,
          },
        },
      },
    },
    notifications: {
      colors: {
        background: {
          neutral: {
            resting: t.background.neutral.raised[5],
            hover: t.background.neutral.raised[5],
          },
          error: {
            resting: t.background.brand.dark[2],
            hover: t.background.semantic.error[4],
          },
          success: {
            resting: t.background.semantic.success[5],
            hover: t.background.semantic.success[4],
          },
          warning: {
            resting: t.background.semantic.warning[5],
            hover: t.background.semantic.warning[4],
          },
          info: {
            resting: t.background.semantic.info[5],
            hover: t.background.semantic.info[4],
          },
          suggest: {
            resting: t.background.brand.dark[2],
            hover: t.background.semantic.suggest[4],
          },
        },
        stroke: {
          neutral: {
            resting: t.stroke.neutral[3],
            hover: t.stroke.neutral[2],
          },
          error: {
            resting: t.stroke.semantic.error[3],
            hover: t.stroke.semantic.error[2],
          },
          success: {
            resting: t.stroke.semantic.success[3],
            hover: t.stroke.semantic.success[2],
          },
          warning: {
            resting: t.stroke.semantic.warning[3],
            hover: t.stroke.semantic.warning[2],
          },
          info: {
            resting: t.stroke.semantic.info[3],
            hover: t.stroke.semantic.info[2],
          },
          suggest: {
            resting: t.stroke.semantic.suggest[3],
            hover: t.stroke.semantic.suggest[2],
          },
        },
        text: {
          title: {
            resting: t.content.text.neutral.base,
          },
          normal: {
            resting: t.content.text.neutral[1],
          },
          static: {
            resting: t.content.text.neutral[1],
          },
        },
        assets: {
          normal: {
            resting: t.content.assets.neutral[1],
            hover: t.content.assets.neutral.base,
            selected: t.content.assets.neutral.base,
          },
          neutral: {
            static: t.content.assets.neutral.base,
          },
          destructive: {
            resting: t.content.assets.semantic.error.base,
          },
          success: {
            resting: t.content.assets.semantic.success.base,
          },
          info: {
            resting: t.content.assets.semantic.info.base,
          },
          warning: {
            static: t.content.assets.semantic.warning.base,
          },
          suggest: {
            static: t.content.assets.semantic.suggest.base,
          },
        },
      },
      details: {
        horizontalPadding: {
          standard: {
            compact: d.spacing.padding.lg,
            base: d.spacing.padding['2xl'],
          },
          zero: {
            zero: d.spacing.padding.zero,
          },
        },
        verticalPadding: {
          standard: {
            sm: d.spacing.padding.base,
            base: d.spacing.padding['2xl'],
          },
          zero: {
            zero: d.spacing.padding.zero,
          },
        },
        spaceBetween: {
          standard: {
            xs: d.spacing.spaceBetween['2xs'],
            sm: d.spacing.spaceBetween.xs,
            base: d.spacing.spaceBetween.base,
            lg: d.spacing.spaceBetween.xl,
            xl: d.spacing.spaceBetween.xs,
          },
          zero: {
            zero: d.spacing.spaceBetween.zero,
          },
        },
        corner: {
          radius: {
            primary: d.cornerRadius.primary,
            sharp: d.cornerRadius.sharp,
          },
        },
        asset: {
          size: {
            compact: d.sizing.asset.sm,
            base: d.sizing.asset.mdBase,
          },
        },
      },
    },
    selectMenuItems: {
      color: {
        item: {
          background: {
            containerBase: t.background.neutral.mid.base,
            header: {
              resting: t.background.basic.clear,
              hover: t.background.basic.clear,
              selected: t.background.neutral.inverse.raised[3],
            },
            row: {
              resting: t.background.basic.clear,
              hover: t.background.neutral.raised[7],
              selected: t.background.neutral.inverse.raised[3],
            },
          },
          stroke: {
            container: {
              resting: t.stroke.basic.clear,
              clear: t.stroke.basic.clear,
            },
            header: {
              resting: t.stroke.basic.clear,
              hover: t.stroke.basic.clear,
              selected: t.stroke.basic.clear,
            },
            row: {
              resting: t.stroke.basic.clear,
              hover: t.stroke.basic.clear,
              selected: t.stroke.basic.clear,
            },
          },
          text: {
            header: {
              resting: t.content.text.neutral[3],
              hover: t.content.text.neutral.base,
              selected: t.content.text.neutral.base,
            },
            row: {
              resting: t.content.text.neutral[1],
              hover: t.content.text.neutral.base,
              selected: t.content.text.neutral.base,
              static: t.content.text.neutral[2],
              blue: t.content.text.accent[14].base,
            },
            rowAlt: {
              selected: t.content.text.brand.base,
            },
          },
          assets: {
            header: {
              resting: t.content.assets.neutral[1],
              hover: t.content.assets.neutral.base,
              selected: t.content.assets.neutral.base,
            },
            row: {
              resting: t.content.assets.neutral.base,
              hover: t.content.assets.neutral.base,
              selected: t.content.assets.neutral.base,
              trailIcon: t.content.assets.neutral[2],
            },
            rowAlt: {
              selected: t.content.assets.brand.base,
            },
          },
        },
      },
      details: {
        height: {
          base: d.sizing.sizeTokens.base,
        },
        horizontalPadding: {
          container: d.spacing.padding.zero,
          header: d.spacing.padding['3xs'],
          row: d.spacing.padding['2xs'],
          subRow: d.spacing.padding.base,
        },
        verticalPadding: {
          base: d.spacing.padding['2xs'],
        },
        spaceBetween: {
          sections: d.spacing.spaceBetween['3xs'],
          rows: d.spacing.spaceBetween['3xs'],
          content: {
            main: d.spacing.spaceBetween.sm,
            secondary: d.spacing.spaceBetween.xs,
          },
        },
        corner: {
          radius: {
            container: d.cornerRadius.primary,
            header: d.cornerRadius.primary,
            row: d.cornerRadius.sharp,
          },
        },
        asset: {
          size: {
            leadTrailIcon: d.sizing.asset.mdBase,
          },
        },
      },
    },
    selectors: {
      colors: {
        checkbox: {
          notSelected: {
            background: {
              resting: t.background.basic.clear,
              hover: t.background.neutral.raised[4],
            },
            stroke: {
              resting: t.stroke.neutral[2],
              hover: t.stroke.neutral[3],
            },
          },
          selected: {
            background: {
              resting: t.background.accent[1][4],
              hover: t.background.accent[1][1],
            },
            stroke: {
              resting: t.stroke.accent[1][1],
              hover: t.stroke.accent[1][2],
            },
            assets: {
              base: t.content.assets.on.color.light.light,
              hover: t.content.assets.on.color.light.light,
            },
          },
        },
        radio: {
          notSelected: {
            background: {
              resting: t.background.neutral.raised[5],
              hover: t.background.neutral.raised[4],
            },
            stroke: {
              resting: t.stroke.neutral[4],
              hover: t.stroke.neutral[3],
            },
          },
          selected: {
            background: {
              resting: t.background.basic.clear,
              hover: t.background.accent[1][4],
            },
            stroke: {
              resting: t.stroke.accent[1][3],
              hover: t.stroke.accent[1].base,
            },
            assets: {
              base: t.content.assets.accent[1].base,
            },
          },
        },
        toggle: {
          off: {
            background: {
              resting: t.background.neutral.raised[5],
              hover: t.background.neutral.raised[4],
            },
            stroke: {
              resting: t.stroke.neutral[4],
              hover: t.stroke.neutral[3],
            },
            assets: {
              resting: t.content.assets.neutral.base,
              hover: t.content.assets.neutral.base,
            },
          },
          on: {
            background: {
              resting: t.background.accent[1][1],
              hover: t.background.accent[1][4],
            },
            stroke: {
              resting: t.stroke.accent[1][1],
              hover: t.stroke.accent[1][2],
            },
            assets: {
              resting: t.content.assets.on.color.light.light,
              hover: t.content.assets.on.color.light.light,
            },
          },
        },
        text: {
          label: {
            notSelected: t.content.text.neutral.base,
          },
          labelSelected: t.content.text.neutral.base,
          description: {
            notSelected: t.content.text.neutral[3],
            selected: t.content.text.neutral[3],
          },
        },
        rangeHandles: {
          background: {
            resting: t.background.neutral.raised.base,
            hover: t.background.brand.dark.base,
            active: t.background.brand.dark.base,
          },
          stroke: {
            resting: t.stroke.basic.clear,
            hover: t.stroke.basic.clear,
            active: t.stroke.basic.clear,
          },
        },
        rangeBar: {
          background: {
            base: t.background.neutral.raised[4],
            hover: t.background.neutral.raised[3],
            active: t.background.neutral.raised[3],
            activeSection: t.background.brand.dark.base,
          },
        },
      },
      details: {
        height: {
          lg: {
            toggle: d.sizing.sizeTokens['3xs'],
          },
          sm: d.sizing.sizeTokens['4xs'],
          rangeBar: d.sizing.dividers.details.tertiary,
          range: {
            handle: d.sizing.asset.sm,
          },
        },
        width: {
          radioCheckbox: {
            lg: d.sizing.sizeTokens['2xs'],
            sm: d.sizing.sizeTokens['4xs'],
          },
          toggle: {
            lg: d.sizing.sizeTokens.sm,
            sm: d.sizing.sizeTokens.xs,
          },
        },
        verticalPadding: {
          lg: d.spacing.padding['4xs'],
          sm: d.spacing.padding.zero,
          zero: d.spacing.padding.zero,
        },
        spaceBetween: {
          horizontal: {
            lg: d.spacing.spaceBetween.sm,
            sm: d.spacing.spaceBetween.sm,
          },
          vertical: {
            lg: d.spacing.spaceBetween['3xs'],
            sm: d.spacing.spaceBetween['3xs'],
          },
        },
        corner: {
          radius: {
            radio: {
              lg: d.cornerRadius.pill,
              sm: d.cornerRadius.pill,
            },
            checkbox: {
              lg: d.cornerRadius.xs,
              sm: d.cornerRadius.xs,
            },
            toggle: {
              lg: d.cornerRadius.pill,
              sm: d.cornerRadius.pill,
            },
            range: {
              lg: d.cornerRadius.sm,
            },
          },
        },
        asset: {
          size: {
            radioCheckbox: {
              lg: '16px',
              sm: '12px',
            },
            toggle: {
              lg: '16px',
              sm: '12px',
            },
          },
        },
      },
    },
    table: {
      colors: {
        background: {
          header: {
            container: {
              resting: neutrals.clear,
              hover: t.background.neutral.raised[4],
              selected: t.background.neutral.raised[3],
            },
            cell: {
              resting: t.background.basic.clear,
              hover: t.background.neutral.raised[4],
              selected: t.background.neutral.raised[3],
            },
          },
          row: {
            container: {
              resting: t.background.basic.clear,
              alternateResting: t.background.basic.clear,
              hover: t.background.neutral.raised[5],
              selected: t.background.neutral.raised[5],
            },
            cell: {
              resting: t.background.basic.clear,
              hover: t.background.basic.clear,
              selected: t.background.basic.clear,
            },
          },
        },
        stroke: {
          header: {
            container: {
              resting: t.stroke.basic.clear,
              hover: t.stroke.basic.clear,
              selected: t.stroke.basic.clear,
            },
            cell: {
              resting: t.stroke.basic.clear,
              hover: t.stroke.basic.clear,
              selected: t.stroke.basic.clear,
            },
          },
          row: {
            container: {
              resting: t.stroke.basic.clear,
              hover: t.stroke.basic.clear,
              selected: t.background.basic.clear,
            },
            cell: {
              resting: t.stroke.basic.clear,
              hover: t.stroke.basic.clear,
              selected: t.stroke.basic.clear,
            },
          },
          divider: {
            base: t.stroke.neutral[4],
          },
        },
        text: {
          header: {
            resting: t.content.text.neutral[3],
            hover: t.content.text.neutral.base,
            selected: t.content.text.neutral.base,
          },
          row: {
            primary: t.contentO.text.neutral.base,
            secondary: t.contentO.text.neutral[2],
            tertiary: t.contentO.text.neutral[3],
          },
        },
        asset: {
          header: {
            resting: t.content.text.neutral[1],
            hover: t.content.text.neutral.base,
            selected: t.content.text.neutral.base,
          },
          row: {
            primary: t.content.text.neutral.base,
            secondary: t.content.text.neutral[2],
            tertiary: t.content.text.neutral[3],
          },
        },
      },
      details: {
        height: {
          cell: {
            regular: d.sizing.sizeTokens.lg,
            condensed: d.sizing.sizeTokens.base,
          },
          header: {
            lg: d.sizing.sizeTokens.lg,
            sm: d.sizing.sizeTokens['2xs'],
          },
          dividingLine: {
            base: d.sizing.dividers.details.base,
          },
        },
        padding: {
          headerContainer: {
            base: d.spacing.padding.xs,
          },
          rowCell: {
            lg: d.spacing.padding.base,
            sm: d.spacing.padding.sm,
          },
          headerCell: {
            lg: d.spacing.padding.base,
            sm: d.spacing.padding.sm,
          },
          rowContainer: {
            lg: d.spacing.padding.xs,
            sm: d.spacing.padding['4xs'],
          },
          dividingLine: {
            lg: d.spacing.padding.xs,
          },
        },
        spaceBetween: {
          columns: d.spacing.spaceBetween.zero,
          rows: d.spacing.spaceBetween.zero,
          innner: {
            primary: d.spacing.spaceBetween.sm,
          },
          innerSecondary: d.spacing.spaceBetween.xs,
          innerTertiary: d.spacing.spaceBetween['3xs'],
        },
        corner: {
          radius: {
            cell: {
              header: d.cornerRadius.primary,
              row: d.cornerRadius.primary,
            },
            container: {
              header: d.cornerRadius.primary,
              row: d.cornerRadius.primary,
            },
            dividingLine: {
              base: d.cornerRadius.primary,
            },
          },
        },
        asset: {
          size: {
            leadTrailIcon: {
              lg: d.sizing.asset.mdBase,
              sm: d.sizing.asset.sm,
              xl: d.sizing.asset.lg,
            },
            sortArrows: {
              lg: d.sizing.asset.sm,
              sm: d.sizing.asset.xs,
            },
          },
        },
      },
    },
    tabs: {
      colors: {
        contained: {
          background: {
            resting: t.background.basic.clear,
            hover: t.background.secondary[4],
            selected: t.background.neutral.midToBrand[2],
            selectedHover: t.background.secondary[3],
          },
          stroke: {
            resting: t.stroke.basic.clear,
            hover: t.stroke.basic.clear,
            selected: t.stroke.basic.clear,
            selectedHover: t.stroke.basic.clear,
          },
          text: {
            resting: t.content.text.neutral[2],
            hover: t.content.text.neutral.base,
            selected: t.content.text.on.color.light.dark,
            selectedHover: t.content.text.on.color.light.dark,
          },
          assets: {
            resting: t.content.assets.neutral[2],
            hover: t.content.assets.neutral.base,
            selected: t.content.assets.on.color.light.dark,
            selectedHover: t.content.assets.accent[14][1],
          },
        },
        underline: {
          background: {
            resting: t.background.basic.clear,
            hover: t.background.neutral.midToBrand[4],
            selected: t.background.neutral.midToBrand[2],
            selectedHover: t.background.neutral.midToBrand[3],
          },
          stroke: {
            resting: t.stroke.basic.clear,
            hover: t.stroke.basic.clear,
            selected: t.stroke.accent[1].base,
            selectedHover: t.background.accent[1][1],
          },
          text: {
            resting: t.content.text.accent[14][2],
            hover: t.content.text.on.color.light.dark,
            selected: t.content.text.on.color.light.dark,
            selectedHover: t.content.text.on.color.light.dark,
          },
          assets: {
            resting: t.content.assets.accent[14][1],
            hover: t.content.assets.neutral.base,
            selected: t.content.assets.on.color.light.dark,
            selectedHover: t.content.assets.neutral.base,
          },
        },
        outline: {
          background: {
            resting: t.background.basic.clear,
            hover: t.background.neutral.raised[5],
            selected: t.background.basic.clear,
            selectedHover: t.background.neutral.raised[5],
          },
          stroke: {
            resting: t.stroke.neutral[5],
            hover: t.stroke.neutral[5],
            selected: t.stroke.brand.base,
            selectedHover: t.stroke.brand[3],
          },
          text: {
            resting: t.content.text.neutral[3],
            hover: t.content.text.neutral.base,
            selected: t.content.text.neutral.base,
            selectedHover: t.content.text.neutral.base,
          },
          assets: {
            resting: t.content.assets.neutral[3],
            hover: t.content.assets.neutral.base,
            selected: t.content.assets.neutral.base,
            selectedHover: t.content.assets.neutral.base,
          },
        },
        textOnly: {
          background: {
            resting: t.background.basic.clear,
            hover: t.background.basic.clear,
            selected: t.background.basic.clear,
            selectedHover: t.background.basic.clear,
          },
          text: {
            resting: t.content.text.neutral[3],
            hover: t.content.text.neutral.base,
            selected: t.content.text.on.color.light.dark,
            selectedHover: t.content.text.accent[14].base,
          },
          assets: {
            resting: t.content.assets.neutral[3],
            hover: t.content.assets.neutral.base,
            selected: t.content.assets.accent[14].base,
            selectedHover: t.content.assets.accent[14][1],
          },
        },
      },
      details: {
        height: {
          lg: d.sizing.sizeTokens.base,
          sm: d.sizing.sizeTokens.sm,
        },
        horizontalPadding: {
          lg: d.spacing.padding.sm,
          sm: d.spacing.padding.xs,
        },
        verticalPadding: {
          lg: d.spacing.padding['3xs'],
          sm: d.spacing.padding['4xs'],
        },
        spaceBetween: {
          lg: d.spacing.spaceBetween.xs,
          sm: d.spacing.spaceBetween['2xs'],
        },
        corner: {
          radius: {
            contained: {
              lg: d.cornerRadius.primary,
              sm: d.cornerRadius.primary,
            },
            underlineDetail: {
              line: d.cornerRadius.primary,
            },
            outline: {
              lg: d.cornerRadius.primary,
              sm: d.cornerRadius.primary,
            },
          },
        },
        asset: {
          size: {
            lg: d.sizing.asset.sm,
            sm: d.sizing.asset.xs,
          },
        },
        strokeWidth: {
          underline: {
            lg: '2px',
            sm: '1px',
          },
        },
      },
    },
    tags: {
      colors: {
        neutral: {
          background: {
            resting: t.background.neutral.raised[1],
            hover: t.background.neutral.raised[4],
            selected: t.background.neutral.raised[3],
          },
          stroke: {
            resting: t.stroke.neutral[4],
            hover: t.stroke.neutral[3],
            selected: t.stroke.neutral[2],
          },
          text: {
            labelBase: t.content.text.on.color.dark.light,
            staticBase: t.content.text.neutral[3],
          },
          assets: {
            leadIconBase: t.content.assets.on.color.light.dark,
            closeBase: t.content.assets.on.color.dark.dark,
          },
        },
        brand: {
          background: {
            resting: t.background.neutral.mid[2],
            hover: t.background.neutral.mid[4],
            selected: t.background.neutral.mid.base,
          },
          stroke: {
            resting: t.stroke.brand[4],
            hover: t.stroke.brand[3],
            selected: t.stroke.brand[2],
          },
          text: {
            labelBase: t.content.text.on.color.light.dark,
            staticBase: t.content.text.neutral[3],
          },
          assets: {
            leadIconBase: t.content.assets.on.color.light.dark,
            closeBase: t.content.assets.neutral[3],
          },
        },
        accent: {
          background: {
            resting: t.background.accent[1][2],
            hover: t.background.accent[1][4],
            selected: t.background.accent[1].base,
          },
          stroke: {
            resting: t.stroke.accent[1][4],
            hover: t.stroke.accent[1][3],
            selected: t.stroke.accent[1][2],
          },
          text: {
            labelBase: t.content.text.on.color.light.light,
            labelSelected: t.content.text.on.color.light.light,
            staticBase: t.content.text.static[3],
          },
          assets: {
            leadIconBase: t.content.assets.on.color.light.dark,
            closeBase: t.content.assets.neutral[3],
          },
        },
        info: {
          background: {
            resting: t.background.semantic.info[1],
            hover: t.background.semantic.info[4],
            selected: t.background.semantic.info[3],
          },
          stroke: {
            resting: t.stroke.semantic.info[4],
            hover: t.stroke.semantic.info[3],
            selected: t.stroke.semantic.info[2],
          },
          text: {
            labelBase: t.content.text.on.color.light.light,
            staticBase: t.content.text.static[3],
          },
          assets: {
            leadIconBase: t.content.assets.on.color.light.dark,
            closeBase: t.content.assets.neutral[3],
          },
        },
        success: {
          background: {
            resting: t.background.semantic.success[1],
            hover: t.background.semantic.success[4],
            selected: t.background.semantic.success[3],
          },
          stroke: {
            resting: t.stroke.semantic.success[4],
            hover: t.stroke.semantic.success[3],
            selected: t.stroke.semantic.success[2],
          },
          text: {
            labelBase: t.content.text.on.color.light.dark,
            staticBase: t.content.text.neutral[3],
          },
          assets: {
            leadIconBase: t.content.assets.on.color.light.dark,
            closeBase: t.content.assets.neutral[3],
          },
        },
        warning: {
          background: {
            resting: t.background.semantic.warning[1],
            hover: t.background.semantic.warning[4],
            selected: t.background.semantic.warning[3],
          },
          stroke: {
            resting: t.stroke.semantic.warning[4],
            hover: t.stroke.semantic.warning[3],
            selected: t.stroke.semantic.warning[2],
          },
          text: {
            labelBase: t.content.text.on.color.light.dark,
            staticBase: t.content.text.neutral[3],
          },
          assets: {
            leadIconBase: t.content.assets.on.color.light.dark,
            closeBase: t.content.assets.neutral[3],
          },
        },
        error: {
          background: {
            resting: t.background.semantic.error[1],
            hover: t.background.semantic.error[4],
            selected: t.background.semantic.error[3],
          },
          stroke: {
            resting: t.stroke.semantic.error[4],
            hover: t.stroke.semantic.error[3],
            selected: t.stroke.semantic.error[2],
          },
          text: {
            labelBase: t.content.text.on.color.light.dark,
            staticBase: t.content.text.neutral[3],
          },
          assets: {
            leadIconBase: t.content.assets.on.color.light.dark,
            closeBase: t.content.assets.neutral[3],
          },
        },
        condition: {
          background: {
            resting: t.background.accent[6][2],
            hover: t.background.accent[6][1],
            selected: t.background.accent[6][2],
          },
          stroke: {
            resting: t.stroke.accent[1][4],
            hover: t.stroke.accent[1][3],
            selected: t.stroke.accent[1][2],
          },
          text: {
            labelBase: t.content.text.on.color.light.dark,
            labelBlue: t.content.text.accent[14].base,
            labelGreen: t.content.text.semantic.success.base,
            staticBase: t.content.text.neutral[3],
          },
          assets: {
            leadIconBase: t.content.assets.on.color.light.dark,
            closeBase: t.content.assets.neutral[3],
          },
        },
        neutral2: {
          background: {
            resting: t.background.neutral.raised[5],
            hover: t.background.neutral.raised[4],
            selected: t.background.neutral.raised[3],
          },
          stroke: {
            resting: t.stroke.neutral[5],
            hover: t.stroke.neutral[4],
            selected: t.stroke.neutral[3],
          },
          text: {
            labelBase: t.content.text.on.color.light.dark,
            staticBase: t.content.text.neutral[3],
          },
          assets: {
            leadIconBase: t.content.assets.on.color.light.dark,
            closeBase: t.content.assets.on.color.light.dark,
          },
        },
      },
      details: {
        height: {
          base: d.sizing.sizeTokens.xs,
        },
        horizontalPadding: {
          base: d.spacing.padding.xs,
        },
        spaceBetween: {
          base: d.spacing.spaceBetween.sm,
          content: d.spacing.spaceBetween['2xs'],
          label: d.spacing.spaceBetween['3xs'],
          detail: d.spacing.spaceBetween.zero,
        },
        corner: {
          radius: {
            base: d.cornerRadius.sm,
          },
        },
        asset: {
          size: {
            icon: d.sizing.asset.mdBase,
            close: d.sizing.asset.sm,
          },
        },
        minWidth: {
          base: d.sizing.sizeTokens.base,
        },
      },
    },
    tooltip: {
      colors: {
        brand: {
          background: {
            default: t.backgroundO.brand.dark.base,
          },
          text: {
            default: t.content.text.on.color.light.dark,
          },
        },
        neutral: {
          background: {
            default: t.backgroundO.neutral.midToWhite.base,
          },
          text: {
            default: t.content.text.on.color.light.dark,
          },
        },
      },
      details: {
        height: {
          lg: d.sizing.sizeTokens['3xs'],
          sm: d.sizing.sizeTokens['4xs'],
        },
        horizontalPadding: {
          sm: d.spacing.padding['2xs'],
          lg: d.spacing.padding.xs,
        },
        corner: {
          radius: {
            corner: {
              radius: d.cornerRadius.sm,
            },
          },
        },
      },
    },
  } as const;
};

// Export type for component tokens
export type ComponentTokens = ReturnType<typeof createTokens>;

// Default export for backward compatibility (uses dark tokens)
export default createTokens(tokensDarkDefault);
